import { useEffect, useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faUserGroup,
    faPlus,
    faChevronDown,
    faCircleInfo,
    faUser,
    faEnvelope,
    faPhone,
    faEuroSign,
    faEye,
    faTrash,
} from "@fortawesome/free-solid-svg-icons";

import CustomForm from "../general/form/CustomForm";
import OptionSelector from "../general/auxiliar/ui/OptionSelector";
import PaginatorComponent from "../general/auxiliar/ui/PaginatorComponent";
import EmptyTable from "../general/auxiliar/table/EmptyTable";
import LoadingTable from "../general/auxiliar/table/LoadingTable";
import OverviewTable from "../general/auxiliar/table/OverviewTable";
import TableBadge from "../general/auxiliar/table/TableBadge";
import WhatsAppReminder from "../general/auxiliar/ui/WhatsAppReminder";
import Dropdown from "../general/auxiliar/ui/Dropdown";
import { getAllPatients } from "../../actions/patient";
import { handleSubmit, debounce, fetchData, getTableState } from "../../auxiliar/auxFunctions";
import {
    isDefined,
    checkMailFormat,
    checkPhoneNumberFormat,
} from "../../auxiliar/formatValidators";

/*
    This component is used to handle the patients associated to an entity (class, voucher)
    - It's more complex than CustomPatientSelector because it has more options and more data to display.
    - Merging both is not great, since this code is overcomplicated for simple use cases.
*/
const CustomPatientsHandler = ({
    register,
    errors,
    watch,
    setValue,
    filter,
    filterAdded,
    maxParticipants,
    disabled,
    operation,
    onExistingClick,
    onAddNewClick,
    onClientSelect,
    onClientRegister,
    onDeletePatient,
    onPaymentClick,
    otherOptionsInHeader,
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const writtenFilter = {
        operation: "displayAssisting",
        name: watch("patient_name") || "",
        surnames: watch("patient_surnames") || "",
        email: watch("patient_email") || "",
        phone_number: watch("patient_phone_number") || "",
        patient_id: watch("patient_id"), // necessary for internal state
    };

    const [debouncedFilter, setDebouncedFilter] = useState(writtenFilter);
    const [patients, setPatients] = useState({ waiting: true });
    const [alreadyAdded, setAlreadyAdded] = useState(0);
    const [assistingPatientsPagination, setAssistingPatientsPagination] = useState({
        per_page: 5,
        page: 1,
        order: "name",
    });

    const processChange = useMemo(
        () => debounce((newFilter) => setDebouncedFilter(newFilter), 300),
        [],
    );

    const onAddPatientClick = (patientId) => {
        if (patientId === "new") {
            // Don't modify patients state when selecting "new" - table will be hidden anyway
        } else {
            // Always set loading state when switching to "existing" to prevent stale data flash
            setPatients({ waiting: true });
        }
        setValue("patient_id", patientId);

        if (onAddNewClick) {
            onAddNewClick();
        }
    };

    const onAlreadyAddedClick = () => {
        if (debouncedFilter.operation !== "displayAssisting") {
            setPatients({ waiting: true });
        }
        setValue("patient_id", undefined);

        if (onExistingClick) {
            onExistingClick();
        }
    };

    const setPatientsAux = (patients) => {
        setPatients(patients);
        setAlreadyAdded(patients?.order?.length || 0);
    };

    // Table Configuration
    const getTableColumns = () => {
        const columns = [
            {
                label: "Nombre y Apellidos",
                icon: faUser,
                width: "w-[70%] sm:w-[35%]", // More space on mobile, less on desktop
            },
            {
                label: "Correo",
                icon: faEnvelope,
                width: "w-[25%]",
                cellClass: "hidden xl:table-cell",
            },
            {
                label: "Teléfono",
                icon: faPhone,
                width: "w-[20%]",
                cellClass: "hidden sm:table-cell",
            },
        ];

        // Add debt column if needed
        if (
            operation.operation === "displayAssisting" &&
            patients.content &&
            Object.keys(patients.content).length > 0
        ) {
            const firstPatient = Object.values(patients.content)[0];
            if (
                firstPatient &&
                (isDefined(firstPatient.debt) || isDefined(firstPatient.patient_debt))
            ) {
                columns.push({
                    label: (
                        <div className="flex items-center gap-2">
                            Deuda
                            <div
                                className="tooltip tooltip-top"
                                data-tip="Por defecto se muestra la deuda de la clase, pero al pasar el ratón por encima se muestra la total"
                            >
                                <FontAwesomeIcon icon={faCircleInfo} />
                            </div>
                        </div>
                    ),
                    icon: faEuroSign,
                    width: "w-[15%]",
                    cellClass: "hidden sm:table-cell",
                });
            }
        }

        return columns;
    };

    const getRowData = (patient, patientId) => {
        // Debt badge logic (consistent with OverviewPatients)
        const patientDebt = parseFloat(patient?.debt || 0);
        const hasDebt = patientDebt > 0;
        const debtText = hasDebt ? `${patientDebt.toFixed(2)}€` : "Pagado";

        const debtVariant = patient.patient_debt > 0 || patient.debt > 0 ? "warning" : "success";

        const rowData = [
            <div
                key={`${patientId}-name`}
                className="font-semibold text-base-content truncate"
            >{`${patient.name} ${patient.surnames}`}</div>,
            <div key={`${patientId}-email`} className="text-base-content/70">
                {patient.email || "-"}
            </div>,
            <div key={`${patientId}-phone`} className="flex items-center">
                <div className="text-base-content/70">{patient.phone_number || "-"}</div>
                <WhatsAppReminder link={patient.link} />
            </div>,
        ];

        // Add debt cell if needed
        if (
            operation.operation === "displayAssisting" &&
            (isDefined(patient.debt) || isDefined(patient.patient_debt))
        ) {
            rowData.push(
                <div
                    key={`${patientId}-debt`}
                    className="tooltip tooltip-top"
                    data-tip={`Deuda total del paciente: ${Number(patient.patient_debt).toFixed(2)}€`}
                >
                    <TableBadge variant={debtVariant} size="sm" className="text-sm cursor-help">
                        {debtText}
                    </TableBadge>
                </div>,
            );
        }

        return rowData;
    };

    const renderActions = (patient, patientId) => {
        if (["displayAssisting", "deletePatient"].includes(operation.operation)) {
            return (
                <Dropdown
                    trigger={
                        <div className="btn btn-primary btn-sm" disabled={disabled}>
                            <FontAwesomeIcon icon={faChevronDown} className="text-base-100" />
                            <span className="hidden lg:inline"> Opciones</span>
                        </div>
                    }
                    items={createPatientActionsDropdownItems(
                        patient,
                        patientId,
                        navigate,
                        onPaymentClick,
                        onDeletePatient,
                        disabled,
                    )}
                    position="right"
                    width="w-52"
                    closeOnItemClick={true}
                    disabled={disabled}
                />
            );
        }

        if (operation.operation === "addPatient") {
            return (
                <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleSubmit(onClientSelect, patient)}
                    disabled={disabled}
                >
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                    Añadir
                </button>
            );
        }

        return null;
    };

    // Selector Configuration
    const selectorOptions = {
        addedPatients: {
            condition: true,
            disabled: disabled,
            selected: operation.operation === "displayAssisting",
            title: isDefined(filterAdded?.group_session_id) ? "Asistentes" : "Autorizados",
            icon: faUserGroup,
            onClick: handleSubmit(onAlreadyAddedClick),
        },
        newPatients: {
            condition: true,
            disabled: disabled,
            title: "Añadir Paciente",
            icon: faPlus,
            selected: operation.operation === "addPatient",
            multiOption: {
                existing_patient: {
                    title: "Existente",
                    condition: !isDefined(filter?.patient_id),
                    disabled: disabled,
                    selected:
                        operation.operation === "addPatient" &&
                        debouncedFilter.patient_id !== "new",
                    onClick: handleSubmit(onAddPatientClick, undefined),
                },
                new_patient: {
                    title: "Nuevo",
                    condition: !isDefined(filter?.patient_id),
                    disabled: disabled,
                    selected:
                        operation.operation === "addPatient" &&
                        debouncedFilter.patient_id === "new",
                    onClick: handleSubmit(onAddPatientClick, "new"),
                },
                register: {
                    title: "Registrar",
                    condition: !isDefined(filter?.patient_id) && isDefined(onClientRegister),
                    disabled:
                        disabled ||
                        debouncedFilter.patient_id !== "new" ||
                        operation.operation !== "addPatient",
                    selected: undefined,
                    onClick: handleSubmit(onClientRegister, { patient_id: "new" }),
                },
            },
        },
        ...otherOptionsInHeader,
    };

    // Form Configuration
    const formConfig = {
        patient_name: {
            condition: true,
            disabled:
                (isDefined(debouncedFilter.patient_id) && debouncedFilter.patient_id !== "new") ||
                isDefined(filter.patient_id),
            type: "text",
            label: `Nombre${debouncedFilter.patient_id === "new" ? " *" : ""}`,
            placeholder: "Nombre",
            name: "patient_name",
            validators: {
                validate: (v) => {
                    if (debouncedFilter.patient_id === "new") {
                        if (v.length < 3) return "El nombre es obligatorio";
                        return true;
                    }
                    return true;
                },
            },
        },
        patient_surnames: {
            condition: true,
            disabled:
                (isDefined(debouncedFilter.patient_id) && debouncedFilter.patient_id !== "new") ||
                isDefined(filter.patient_id),
            type: "text",
            label: `Apellidos${debouncedFilter.patient_id === "new" ? " *" : ""}`,
            placeholder: "Apellidos",
            name: "patient_surnames",
            validators: {
                validate: (v) => {
                    if (debouncedFilter.patient_id === "new") {
                        if (v.length < 3) return "Los apellidos son obligatorios";
                        return true;
                    }
                    return true;
                },
            },
        },
        patient_email: {
            condition: true,
            disabled:
                (isDefined(debouncedFilter.patient_id) && debouncedFilter.patient_id !== "new") ||
                isDefined(filter.patient_id),
            type: "text",
            label: "Correo Electrónico",
            placeholder: "paciente@email.com",
            name: "patient_email",
            validators: {
                validate: (v) => {
                    if (debouncedFilter.patient_id === "new") {
                        return !isDefined(v) || checkMailFormat(v);
                    } else {
                        return true;
                    }
                },
            },
        },
        patient_phone_number: {
            condition: true,
            disabled:
                (isDefined(debouncedFilter.patient_id) && debouncedFilter.patient_id !== "new") ||
                isDefined(filter.patient_id),
            type: "text",
            label: "Número de teléfono",
            placeholder: "+34 XXX XXX XXX",
            name: "patient_phone_number",
            validators: {
                validate: (v) => {
                    if (debouncedFilter.patient_id === "new") {
                        return !isDefined(v) || checkPhoneNumberFormat(v);
                    } else {
                        return true;
                    }
                },
            },
        },
    };

    useEffect(() => {
        // If the class changes or operation changes, the loading visual is displayed
        if (
            filterAdded.group_session_id !== debouncedFilter.group_session_id ||
            operation.operation !== debouncedFilter.operation
        ) {
            setPatients({ waiting: true });
        }

        const newFilter = {
            ...filterAdded,
            ...writtenFilter,
            operation: operation.operation,
            update: operation.update,
        };
        if (JSON.stringify(debouncedFilter) !== JSON.stringify(newFilter)) {
            processChange(newFilter);
        }
    }, [JSON.stringify(writtenFilter), filterAdded, operation.operation, operation.update]);

    useEffect(() => {
        if (!isDefined(debouncedFilter.clinic_id)) return;

        if (debouncedFilter.operation === "displayAssisting") {
            fetchData(
                dispatch,
                getAllPatients,
                {
                    ...filterAdded,
                    ...assistingPatientsPagination,
                },
                setPatientsAux,
            );
        } else if (
            debouncedFilter.patient_id !== "new" &&
            debouncedFilter.operation === "addPatient"
        ) {
            fetchData(
                dispatch,
                getAllPatients,
                {
                    ...filter,
                    ...assistingPatientsPagination,
                    name: debouncedFilter.name,
                    surnames: debouncedFilter.surnames,
                    email: debouncedFilter.email,
                    phone_number: debouncedFilter.phone_number,
                },
                setPatients,
            );
        }
    }, [debouncedFilter, assistingPatientsPagination, dispatch, setPatients]);

    return (
        <form className="my-3 mx-auto">
            <div className="flex justify-between">
                <h2 className="text-primary text-xl flex items-center">
                    {["addPatient", "displayAssisting"].includes(operation.operation) &&
                        `${isDefined(filterAdded?.group_session_id) ? "Asistentes" : "Autorizados"} (${alreadyAdded}/${maxParticipants || "∞"})`}
                    {["pay"].includes(operation.operation) && (
                        <>
                            {operation.metadata.display === "cash_register_return"
                                ? "Devolución"
                                : "Pago"}
                            :{" "}
                            {`${operation.metadata.name || ""} ${operation.metadata.surnames || ""}`}
                        </>
                    )}
                </h2>

                <div className="flex gap-3 md:justify-end">
                    <OptionSelector options={selectorOptions} />
                </div>
            </div>

            {/* if adding a patient the filter is displayed */}
            {operation.operation === "addPatient" && (
                <form className="my-2">
                    <CustomForm
                        formConfig={formConfig}
                        errors={errors}
                        register={register}
                        className="grid grid-cols-1 md:grid-cols-2 gap-3"
                    />
                </form>
            )}

            {/* Patients table if not registering a new patient */}
            <div className="mt-4">
                {["addPatient", "displayAssisting", "deletePatient"].includes(
                    operation.operation,
                ) &&
                    debouncedFilter.patient_id !== "new" &&
                    (() => {
                        const tableState = getTableState(patients);

                        switch (tableState) {
                            case "loading":
                                return <LoadingTable message="Cargando pacientes..." />;

                            case "empty":
                                return (
                                    <EmptyTable
                                        icon={faUser}
                                        title="No hay pacientes registrados"
                                        description="No hay pacientes registrados con estos datos."
                                    />
                                );

                            case "hasData":
                            default:
                                return (
                                    <div>
                                        {/* Pagination for different operations */}
                                        <div className="mb-4 flex justify-end">
                                            <PaginatorComponent
                                                pagination={patients.pagination}
                                                setFilter={setAssistingPatientsPagination}
                                                filter={assistingPatientsPagination}
                                                disabled={disabled}
                                                handleSubmitWithBlock={handleSubmit}
                                            />
                                        </div>

                                        <OverviewTable
                                            data={patients}
                                            columns={getTableColumns()}
                                            getRowData={getRowData}
                                            renderActions={renderActions}
                                            className="!block" // Force show table on all screen sizes
                                            actionsWidth="w-[30%] sm:w-[10%]" // More space for actions on mobile
                                            noTruncateColumns={
                                                getTableColumns().length > 3
                                                    ? [getTableColumns().length - 1]
                                                    : []
                                            } // Don't truncate debt column if it exists
                                        />
                                    </div>
                                );
                        }
                    })()}
            </div>
        </form>
    );
};

// Helper function to create patient actions dropdown items
const createPatientActionsDropdownItems = (
    patient,
    patientId,
    navigate,
    onPaymentClick,
    onDeletePatient,
    disabled,
) => {
    const items = [
        {
            key: "details",
            label: (
                <div className="flex items-center">
                    <FontAwesomeIcon icon={faEye} className="mr-2" />
                    Detalles
                </div>
            ),
            onClick: () => handleSubmit(navigate, `/patient/${patientId}`)(),
        },
    ];

    // Add payment option if available
    if (onPaymentClick) {
        items.push({
            key: "payment",
            label: (
                <div className="flex items-center">
                    <FontAwesomeIcon icon={faEuroSign} className="mr-2" />
                    Pago
                </div>
            ),
            onClick: () => onPaymentClick(patient)(),
            disabled,
        });
    }

    // Add delete option
    items.push({
        key: "delete",
        label: (
            <div className="flex items-center">
                <FontAwesomeIcon icon={faTrash} className="mr-2" />
                Eliminar
            </div>
        ),
        onClick: () =>
            handleSubmit(onDeletePatient, {
                patient_id: patientId,
                recurrent: patient.recurrent,
            })(),
        disabled: disabled || patient.role === "Owner",
    });

    return items;
};

export default CustomPatientsHandler;
